/**
 * Product Detail Page Helpers
 * All business logic, data processing, and utility functions
 */

import { AWS_URL } from "@/constants";

// ==================== IMAGE UTILITIES ====================

export const defaultImage = "/default/product.png";

/**
 * Extract product image URL from media object
 */
export const getProductUrl = (list) => {
  if (!list || list.length === 0) return null;
  const first = list[0];
  const media = first?.media || first;
  if (!media?.path) return null;
  return `${AWS_URL}${media.path}`;
};

/**
 * Build gallery images based on product and variant selection
 */
export const buildGalleryImages = (apiProduct, selectedVariant) => {
  if (!apiProduct) return [defaultImage];

  let images = [];

  // CASE 1: Selected variant → show variant-specific images
  if (selectedVariant && selectedVariant.product_media?.length > 0) {
    images = selectedVariant.product_media
      .map((m) => getProductUrl([m.media]))
      .filter(Boolean);
  }

  // CASE 2: No variant or no variant images → show product-level images
  if (images.length === 0 && apiProduct.product_media?.length > 0) {
    images = apiProduct.product_media
      .map((pm) => getProductUrl([pm]))
      .filter(Boolean);
  }

  // CASE 3: If still no images → default image
  if (images.length === 0) {
    images = [defaultImage];
  }

  return images;
};

// ==================== VARIANT FILTERING ====================

/**
 * Filter available variants based on media presence
 */
export const getAvailableVariants = (apiProduct) => {
  if (!apiProduct?.product_variations) return [];

  const hasProductMedia = apiProduct?.product_media?.length > 0;

  const variants = hasProductMedia
    ? apiProduct.product_variations.filter((v) => v?.product_media?.length > 0)
    : apiProduct.product_variations;

  return variants.filter((variant) => {
    if (
      !variant.product_variant_sizes ||
      variant.product_variant_sizes.length === 0
    ) {
      return (variant.available_quantity ?? 0) > 0;
    }
    return variant.product_variant_sizes.some((size) => (size.quantity ?? 0) > 0);
  });
};

/**
 * Get first variant ID for auto-selection
 */
export const getFirstVariantId = (apiProduct, availableVariants) => {
  if (apiProduct?.has_variation && availableVariants.length > 0) {
    return availableVariants[0].id;
  }
  return null;
};

// ==================== CURRENCY & PRICING ====================

/**
 * Check if currency is INR
 */
export const isINRCurrency = (selectedCurrency) => {
  return (
    selectedCurrency?.name === "inr" || selectedCurrency?.code === "inr"
  );
};

/**
 * Calculate weight-based price
 */
export const calculateWeightBasedPrice = (weight, pricePerGram) => {
  return Number((weight * pricePerGram).toFixed(2));
};

/**
 * Calculate base price for product
 */
export const calculateBasePrice = (apiProduct, selectedVariant) => {
  const pricePerGram = apiProduct?.price_per_gm || 0;
console.log("prrce",pricePerGram)
  if (!apiProduct?.has_variation) {
    return calculateWeightBasedPrice(apiProduct?.dim_weight || 0, pricePerGram);
  }

  let basePrice = selectedVariant?.sale_price ?? selectedVariant?.actual_price;

  if (!basePrice && selectedVariant?.product_variant_sizes?.length > 0) {
    const firstSize = selectedVariant.product_variant_sizes[0];
    basePrice = calculateWeightBasedPrice(firstSize.weight, pricePerGram);
  }

  return basePrice;
};

/**
 * Convert price to selected currency
 */
export const convertPrice = (price, conversionRate, isINR = false) => {
  if (!price) return null;
  return isINR
    ? parseFloat(price.toFixed(2))
    : parseFloat((price / conversionRate).toFixed(2));
};

// ==================== SIZE DISPLAY ====================

/**
 * Get display size based on currency and size object
 */
export const getDisplaySize = (sizeObj, showUSSize) => {
  if (!sizeObj) return "N/A";

  // Non-ring product — has no ring sizes at all
  if (sizeObj.ring_size === null && sizeObj.ring_inr_size === null) {
    return sizeObj.size_value || "N/A";
  }

  // Ring product — show the right size system, no cross-fallback
  if (showUSSize) {
    return sizeObj.ring_size ?? sizeObj.size_value ?? "N/A";
  }

  return sizeObj.ring_inr_size ?? sizeObj.ring_size ?? sizeObj.size_value ?? "N/A";
};

// ==================== VARIANT DISPLAY ====================

/**
 * Get gemstone names from variant, filtering out NULL values
 */
export const getGemstoneNames = (variant) => {
  return variant?.product_variation_gems
    ?.map((vg) => vg.gem?.name)
    .filter((name) => name && name.toUpperCase() !== "NULL")
    .join(", ");
};

/**
 * Get variant display info (image, name, has gemstones)
 */
export const getVariantDisplayInfo = (variant) => {
  const variantImage =
    variant.product_media?.length > 0
      ? getProductUrl([variant.product_media[0].media])
      : null;

  const gemstoneNames = getGemstoneNames(variant);
  const hasValidGemstones = gemstoneNames && gemstoneNames.length > 0;

  return {
    image: variantImage,
    gemstoneNames,
    hasValidGemstones,
    displayName: variant.value || gemstoneNames || `Variant ${variant.id}`,
  };
};

// ==================== CART VALIDATION ====================

/**
 * Validate quantity selections against MOQ
 */
export const validateQuantities = (
  apiProduct,
  wholesaleSelections,
  variantSizeIds,
  existingCartQty = 0
) => {
  const moq = apiProduct?.min_order_quantity ?? 1;
  const platingMoq = apiProduct?.min_order_plating ?? 1;

  const variantSelections = wholesaleSelections.filter((row) =>
    variantSizeIds.includes(row.variantSizeId)
  );

  const totalSelectedQty = variantSelections.reduce(
    (a, r) => a + (r.qty || 0),
    0
  );

  const totalPlatingQty = variantSelections.reduce(
    (a, r) => (r.plating_id ? a + (r.qty || 0) : a),
    0
  );

  const errors = [];

  if (totalSelectedQty + existingCartQty < moq) {
    errors.push(
      `Minimum order quantity is ${moq} pcs. You selected ${totalSelectedQty}${
        existingCartQty > 0 ? ` (${existingCartQty} already in cart)` : ""
      }.`
    );
  }

  if (totalPlatingQty > 0 && totalPlatingQty < platingMoq) {
    errors.push(
      `Minimum plating quantity is ${platingMoq} pcs. You selected ${totalPlatingQty}.`
    );
  }

  return { isValid: errors.length === 0, errors, variantSelections };
};

// ==================== CART PAYLOAD BUILDERS ====================

/**
 * Build cart payload for non-variation product
 */
export const buildNoVariationPayload = (
  product_id,
  apiProduct,
  noVariationQty,
  selectedImage,
  pricePerGram
) => {
  const payload = {
    product_id,
    product_variation_id: null,
    variant_size_id: null,
    quantity: noVariationQty,
    id: product_id,
    name: apiProduct.name,
    size: "N/A",
    ring_size: null,
    ring_inr_size: null,
    metal: "",
    weight: apiProduct?.dim_weight || 0,
    pricePerGram: pricePerGram,
    // NEW: modifyItemsInCartAPI.js reads `product.price_per_gm` (snake_case)
    // when saving the cart item. Mirror the value under that key too rather
    // than renaming `pricePerGram` above, since other code (checkout calcs,
    // discount helpers, cart slice) still depends on the camelCase field.
    price_per_gm: pricePerGram,
    image: selectedImage,
    wholesaleSelections: [],
      category_id: apiProduct?.category?.id || null,
  };

  if (
    apiProduct?.available_quantity !== undefined &&
    apiProduct.available_quantity !== null
  ) {
    payload.maxQty = apiProduct.available_quantity;
  }

  return payload;
};

/**
 * Build cart payload for variation product
 */
export const buildVariationPayload = (
  product_id,
  selectedVariant,
  selection,
  displayName,
  apiProduct,    
  selectedImage,
  pricePerGram,
  showUSSize
) => {
  const sizeObj = selectedVariant?.product_variant_sizes?.find(
    (s) => s.id === selection.variantSizeId
  );

  const displaySize =
    selection.display_size || getDisplaySize(sizeObj, showUSSize);

  const payload = {
    product_id,
    product_variation_id: selectedVariant?.id,
    variant_size_id: selection.variantSizeId,
    quantity: selection.qty,
    id: product_id,
    name: displayName,
    size: displaySize,
    ring_size: selection.ring_size,
    ring_inr_size: selection.ring_inr_size,
    metal: selection.metal || "",
    weight: selection.weight || 0,
    pricePerGram: pricePerGram,
    // NEW: mirror for modifyItemsInCartAPI.js — see note above.
    price_per_gm: pricePerGram,
      category_id: apiProduct?.category?.id || null,
    image: selectedImage,
    plating_id: selection.plating_id || null,
    plating_name: selection.plating_name || "",
    plating_price_per_gram: selection.plating_price_per_gram || 0,
    original_stock: sizeObj.original_stock,
    wholesaleSelections: [selection],
  };

  if (sizeObj?.quantity !== undefined && sizeObj.quantity !== null) {
    payload.maxQty = sizeObj.quantity;
  }

  return payload;
};

// ==================== WHOLESALE ROW UPDATES ====================

/**
 * Update a wholesale selection row
 */
export const updateWholesaleRow = (
  prevSelections,
  variantSizeId,
  usdSize,
  inrSize,
  updates
) => {
  const index = prevSelections.findIndex(
    (row) => row.variantSizeId === variantSizeId
  );

  const newRow = {
    ...prevSelections[index],
    variantSizeId,
    ring_size: usdSize,
    ring_inr_size: inrSize,
    plating_id: null,
    plating_name: "",
    plating_price_per_gram: 0,
    ...updates,
  };

  if (index > -1) {
    const list = [...prevSelections];
    list[index] = newRow;
    return list;
  }

  return [...prevSelections, newRow];
};

// ==================== SOLD OUT CHECK ====================

/**
 * Check if product is sold out
 */
export const checkSoldOut = (apiProduct, availableVariants) => {
  if (!apiProduct?.has_variation) return false;

  return !availableVariants.some((v) =>
    v.product_variant_sizes?.some((sz) => sz.quantity > 0)
  );
};

// ==================== TABLE CALCULATIONS ====================

/**
 * Calculate max height for size selection table
 */
export const calculateTableMaxHeight = (sizesCount) => {
  return Math.min(sizesCount * 50 + 70, 500);
};

/**
 * Generate quantity options for a size
 */
export const generateQuantityOptions = (maxQuantity) => {
  return Array.from(
    { length: Math.max(0, Number(maxQuantity) || 0) },
    (_, i) => i + 1
  );
};

// ==================== CART QUANTITY AGGREGATION ====================

/**
 * Calculate existing cart quantity for a given product across all cart items.
 * Handles both wholesale (multi-size) and regular cart items.
 */
export const getExistingCartQty = (cartProducts, product_id) => {
  if (!cartProducts?.length) return 0;
  return cartProducts
    .filter((p) => String(p.product_id) === String(product_id))
    .reduce((sum, p) => {
      if (p.wholesaleSelections?.length > 0) {
        return (
          sum + p.wholesaleSelections.reduce((s, sel) => s + (sel.qty || 0), 0)
        );
      }
      return sum + (p.quantity || 0);
    }, 0);
};

// ==================== WHOLESALE TOTALS ====================

/**
 * Count how many variants have at least one selected quantity.
 */
export const getWholesaleTotalVariants = (multiVariantSelections) => {
  return Object.values(multiVariantSelections).filter((selections) =>
    selections.some((s) => s.qty > 0)
  ).length;
};

/**
 * Sum all selected quantities across all variants.
 */
export const getWholesaleTotalQty = (multiVariantSelections) => {
  return Object.values(multiVariantSelections).reduce(
    (total, selections) =>
      total + selections.reduce((sum, s) => sum + (s.qty || 0), 0),
    0
  );
};

// ==================== ADD TO CART HANDLER ====================

/**
 * Build and dispatch add-to-cart action(s) based on product mode.
 *
 * @param {object} opts
 * @param {string}   opts.product_id
 * @param {object}   opts.apiProduct
 * @param {object[]} opts.availableVariants
 * @param {string}   opts.selectedVariantId
 * @param {object}   opts.selectedVariant
 * @param {boolean}  opts.isWholesaleMode
 * @param {object}   opts.multiVariantSelections   – wholesale multi-variant map
 * @param {object[]} opts.wholesaleSelections       – single-variant size rows
 * @param {number}   opts.noVariationQty
 * @param {number}   opts.existingCartQty
 * @param {string}   opts.selectedImage
 * @param {number}   opts.pricePerGram
 * @param {string}   opts.token                    – auth token (null if logged out)
 * @param {string}   opts.pathname
 * @param {function} opts.dispatch                 – Redux dispatch
 * @param {function} opts.addToCartWithAPI         – Redux thunk action creator
 * @param {function} opts.successNotification
 * @param {function} opts.errorNotification
 * @param {function} opts.refetch                  – Apollo refetch
 * @param {function} opts.router                   – Next.js router
 * @param {function} opts.onLoginRedirect          – called with (payload|null) for single item
 * @param {function} opts.onLoginRedirectWholesale – called with (payloads[]) for wholesale
 * @param {function} opts.onClearWholesale         – reset multiVariantSelections
 * @param {function} opts.onClearSingleWholesale   – reset wholesaleSelections
 */
export const handleAddToCart = async ({
  product_id,
  apiProduct,
  availableVariants,
  selectedVariantId,
  selectedVariant,
  isWholesaleMode,
  multiVariantSelections,
  wholesaleSelections,
  noVariationQty,
  existingCartQty,
  selectedImage,
  pricePerGram,
  token,
  pathname,
  dispatch,
  addToCartWithAPI,
  successNotification,
  errorNotification,
  refetch,
  router,
  onLoginRedirect,
  onLoginRedirectWholesale,
  onClearWholesale,
  onClearSingleWholesale,
}) => {
  // ── WHOLESALE MULTI-VARIANT MODE ─────────────────────────────────────────
  if (isWholesaleMode && apiProduct?.has_variation) {
    const allVariantEntries = Object.entries(multiVariantSelections);
    const variantsWithSelections = allVariantEntries.filter(([, selections]) =>
      selections.some((s) => s.qty > 0)
    );

    if (variantsWithSelections.length === 0) {
      return errorNotification("Please select at least one size and quantity");
    }

    const moq = apiProduct?.min_order_quantity ?? 1;
    const grandTotalQty = variantsWithSelections.reduce(
      (total, [, selections]) =>
        total + selections.reduce((sum, s) => sum + (s.qty || 0), 0),
      0
    );

    if (grandTotalQty + existingCartQty < moq) {
      return errorNotification(
        `Minimum order quantity is ${moq} pcs total across all gemstones. You selected ${grandTotalQty}${
          existingCartQty > 0 ? ` (${existingCartQty} already in cart)` : ""
        }.`
      );
    }

    const platingMoq = apiProduct?.min_order_plating ?? 1;
    const grandTotalPlatingQty = variantsWithSelections.reduce(
      (total, [, selections]) =>
        total +
        selections.reduce((sum, s) => (s.plating_id ? sum + (s.qty || 0) : sum), 0),
      0
    );

    if (grandTotalPlatingQty > 0 && grandTotalPlatingQty < platingMoq) {
      return errorNotification(
        `Minimum plating quantity is ${platingMoq} pcs total. You selected ${grandTotalPlatingQty}.`
      );
    }

    // Build payloads array
    const payloads = [];

    for (const [variantId, selections] of variantsWithSelections) {
      const variant = availableVariants.find((v) => v.id === variantId);
      if (!variant) continue;

      const nonZeroSelections = selections.filter((s) => s.qty > 0);
      if (nonZeroSelections.length === 0) continue;

      const enrichedSelections = nonZeroSelections.map((selection) => {
        const sizeObj = variant.product_variant_sizes?.find(
          (s) => s.id === selection.variantSizeId
        );
        return { ...selection, original_stock: sizeObj?.quantity || 0 };
      });
const variantImg =
  variant.product_media?.[0]?.media
    ? getProductUrl([variant.product_media[0].media])
    : selectedImage; // fallback to product-level image
      payloads.push({
        product_id,
        product_variation_id: variantId,
        variant_size_id: null,
          category_id: apiProduct?.category?.id || null,
        quantity: enrichedSelections.reduce((sum, s) => sum + s.qty, 0),
        id: product_id,
        name: variant.value || apiProduct.name,
        size: "Multiple",
        ring_size: null,
        min_order_quantity: apiProduct?.min_order_quantity || 1,
        ring_inr_size: null,
        metal: enrichedSelections[0]?.metal || "",
        weight: 0,
        pricePerGram,
        // NEW: mirror for modifyItemsInCartAPI.js — see note above.
        price_per_gm: pricePerGram,
        image: variantImg,
        item_code: apiProduct.item_code,
        wholesaleSelections: enrichedSelections,
        sub_category_name: apiProduct?.sub_category?.name ?? "",
      });
    }

    if (payloads.length === 0) {
      return errorNotification(
        "Please select at least one item with quantity greater than 0"
      );
    }

    if (!token) {
      onLoginRedirectWholesale(payloads);
      return;
    }

    try {
      for (const payload of payloads) {
        await dispatch(addToCartWithAPI(payload)).unwrap();
      }
      successNotification(`Added ${payloads.length} variant(s) to cart successfully!`);
      onClearWholesale();
      refetch();
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.message || error?.error || "Failed to add to cart";
      errorNotification(msg);
      if (
        msg.includes("stock has changed") ||
        msg.includes("no longer available") ||
        msg.includes("now out of stock")
      ) {
        refetch();
        onClearWholesale();
      }
    }
    return;
  }

  // ── NO VARIATION ─────────────────────────────────────────────────────────
  if (!apiProduct?.has_variation) {
    const payload = buildNoVariationPayload(
      product_id,
      apiProduct,
      noVariationQty,
      selectedImage,
      pricePerGram
    );
    payload.min_order_quantity = apiProduct?.min_order_quantity || 1;

    const moq = apiProduct?.min_order_quantity ?? 1;
    if (noVariationQty + existingCartQty < moq) {
      return errorNotification(
        `Minimum order quantity is ${moq} pcs.${
          existingCartQty > 0 ? ` You have ${existingCartQty} in cart already.` : ""
        }`
      );
    }

    if (!token) {
      onLoginRedirect(payload);
      return;
    }

    try {
      await dispatch(addToCartWithAPI(payload)).unwrap();
      successNotification("Added to cart successfully!");
      refetch();
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.message || error?.error || "Failed to add to cart";
      errorNotification(msg);
    }
    return;
  }

  // ── VARIATION – NO SIZES ─────────────────────────────────────────────────
  const hasSizes = selectedVariant?.product_variant_sizes?.length > 0;

  if (!hasSizes) {
    const payload = {
      product_id,
      product_variation_id: selectedVariant?.id,
      variant_size_id: null,
        category_id: apiProduct?.category?.id || null,
      quantity: noVariationQty,
      id: product_id,
      name: selectedVariant?.value || apiProduct.name,
      min_order_quantity: apiProduct?.min_order_quantity || 1,
      size: "N/A",
      ring_size: null,
      ring_inr_size: null,
      metal: selectedVariant?.metal || "",
      weight: selectedVariant?.weight || apiProduct?.dim_weight || 0,
      pricePerGram,
      // NEW: mirror for modifyItemsInCartAPI.js — see note above.
      price_per_gm: pricePerGram,
      image: selectedImage,
      sub_category_name: apiProduct?.sub_category?.name ?? "",
      wholesaleSelections: [],
    };

    if (
      selectedVariant?.available_quantity !== undefined &&
      selectedVariant.available_quantity !== null
    ) {
      payload.maxQty = selectedVariant.available_quantity;
    }

    const moq = apiProduct?.min_order_quantity ?? 1;
    if (noVariationQty + existingCartQty < moq) {
      return errorNotification(
        `Minimum order quantity is ${moq} pcs.${
          existingCartQty > 0 ? ` You have ${existingCartQty} in cart already.` : ""
        }`
      );
    }

    if (!token) {
      onLoginRedirect(payload);
      return;
    }

    try {
      await dispatch(addToCartWithAPI(payload)).unwrap();
      successNotification("Added to cart successfully!");
      refetch();
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.message || error?.error || "Failed to add to cart";
      errorNotification(msg);
    }
    return;
  }

  // ── VARIATION – WITH SIZES ────────────────────────────────────────────────
  const variantSizeIds = selectedVariant.product_variant_sizes.map((s) => s.id);

  const { isValid, errors, variantSelections } = validateQuantities(
    apiProduct,
    wholesaleSelections,
    variantSizeIds,
    existingCartQty
  );
  if (!isValid) return errorNotification(errors[0]);

  const nonZeroSelections = variantSelections.filter((s) => s.qty > 0);

  if (nonZeroSelections.length === 0) {
    return errorNotification(
      "Please select at least one item with quantity greater than 0"
    );
  }

  const enrichedSelections = nonZeroSelections.map((selection) => {
    const sizeObj = selectedVariant.product_variant_sizes.find(
      (s) => s.id === selection.variantSizeId
    );
    return { ...selection, original_stock: sizeObj?.quantity || 0 };
  });

  const displayName = selectedVariant?.value || apiProduct.name;

  const payload = {
    product_id,
    product_variation_id: selectedVariant?.id,
    variant_size_id: null,
    quantity: enrichedSelections.reduce((sum, s) => sum + s.qty, 0),
    id: product_id,
    name: displayName,
    size: "Multiple",
    ring_size: null,
      category_id: apiProduct?.category?.id || null,
    min_order_quantity: apiProduct?.min_order_quantity || 1,
    ring_inr_size: null,
    metal: enrichedSelections[0]?.metal || "",
    weight: 0,
    pricePerGram,
    // NEW: mirror for modifyItemsInCartAPI.js — see note above.
    price_per_gm: pricePerGram,
    image: selectedImage,
    item_code: apiProduct.item_code,
    sub_category_name: apiProduct?.sub_category?.name ?? "",
    wholesaleSelections: enrichedSelections,
  };

  if (!token) {
    onLoginRedirect(payload);
    return;
  }

  try {
    await dispatch(addToCartWithAPI(payload)).unwrap();
    successNotification("Added to cart successfully!");
    onClearSingleWholesale();
    refetch();
  } catch (error) {
    const msg =
      typeof error === "string"
        ? error
        : error?.message || error?.error || "Failed to add to cart";
    errorNotification(msg);
    if (
      msg.includes("stock has changed") ||
      msg.includes("no longer available") ||
      msg.includes("now out of stock")
    ) {
      refetch();
      onClearSingleWholesale();
    }
  }
};